/**
 * Team → Created today (founder, AD) — prototype VIEWS.newtoday (docs/prototype.html L2610): four stats (hidden in
 * the Team hub by the prototype CSS, as there), the "still waiting for the SPOC" note, the Source dropdown (All sources,
 * or any set of sources with their counts; Email always listed; t-da3a924a), and the table: Task (client under it), Source, Given to, Due,
 * Status, Time. Newest first.
 *
 * Data: GET /team/created-today: tasks made today, every email proposal still pending, and what was left out today.
 * A task row opens the task; a proposal row goes to the Review queue, where proposals are confirmed or rejected.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { DROP_REASON_LABEL, TASK_SOURCE_LABEL, type CreatedTodayRow, type TaskSource } from '@amplispot/contracts';
import { firstName, todayIso } from '../../lib/format';
import { useCreatedToday } from '../../lib/team';
import { dueText } from '../../lib/work';
import { Avatar, ErrorState, Loading, MultiSelectFilter } from '../../ui/kit';
import { Lbl } from '../../shell/Help';
import { TaskPanel } from '../work/TaskPanel';
import { Stat } from '../timesheets/TimesheetReport';

/** Prototype SRCN (its words on this screen), in its order; other sources use the shared labels. */
const SRCN: Partial<Record<TaskSource, string>> = {
  email: 'Email',
  mom: 'Meeting minutes',
  voice: 'Voice note',
  request: 'Client request',
  adhoc: 'Added by hand',
  checklist: 'Routine',
};
const srcLabel = (s: TaskSource) => SRCN[s] ?? TASK_SOURCE_LABEL[s];
const ORDER = [...(Object.keys(SRCN) as TaskSource[]), ...(Object.keys(TASK_SOURCE_LABEL) as TaskSource[]).filter((k) => !(k in SRCN))];

const time = (ts: string) => new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Kolkata' }).format(new Date(ts));

function status(r: CreatedTodayRow): [string, string] {
  switch (r.status) {
    case 'done':
      return ['hot', 'Done'];
    case 'active':
      return ['info', 'Active'];
    case 'waiting':
      return ['cold', `Waiting ${r.waiting_hours ?? 0}h`];
    case 'proposed':
      return ['warm', 'Proposed'];
    default:
      return ['', `Left out: ${r.left_out_reason ? DROP_REASON_LABEL[r.left_out_reason] : 'no reason given'}`];
  }
}

export function CreatedToday() {
  const q = useCreatedToday();
  const nav = useNavigate();
  /** Picked sources; empty = All sources. On screen over the whole day (GET /team/created-today is not paged). */
  const [src, setSrc] = useState<TaskSource[]>([]);
  const [task, setTask] = useState<string | null>(null);
  const today = todayIso();

  if (q.isPending) return <Loading label="Loading what was created today…" />;
  if (q.isError) return <ErrorState error={q.error} onRetry={() => q.refetch()} />;
  const { totals, by_source, rows: all, late_hours } = q.data;
  const rows = !src.length ? all : all.filter((r) => src.includes(r.source));
  const late = all.filter((r) => r.status === 'waiting');
  const sources = ORDER.filter((k) => by_source[k] || k === 'email' || all.some((r) => r.source === k));

  return (
    <>
      <div className="stats">
        <Stat k="Made today" v={totals.made} s="tasks" cls="green" />
        <Stat k="From emails" v={totals.from_email} s={`${totals.waiting_spoc} waiting for a SPOC`} />
        <Stat k="Waiting too long" v={totals.waiting_too_long} s={`${late_hours}+ hours`} cls="red" />
        <Stat k="Left out today" v={totals.left_out} s="turned down" />
      </div>
      {late.length ? (
        <div className="note warn" style={{ marginBottom: 14 }}>
          <b>
            {late.length} email task{late.length > 1 ? 's are' : ' is'} still waiting for the SPOC:
          </b>{' '}
          {late.map((r) => `${r.assignee ? firstName(r.assignee.name) : 'No SPOC'} (${r.client?.name ?? 'Internal'}, ${r.waiting_hours ?? 0}h)`).join(', ')}.
        </div>
      ) : null}
      <div className="bar">
        <MultiSelectFilter<TaskSource>
          label="Source"
          allLabel="All sources"
          options={sources.map((k) => ({ value: k, label: srcLabel(k), count: by_source[k] || undefined }))}
          value={src}
          onChange={setSrc}
        />
      </div>
      <div className="panel">
        <div style={{ overflowX: 'auto' }}>
          <table>
            <thead>
              <tr>
                <th><Lbl>Task</Lbl></th>
                <th><Lbl>Source</Lbl></th>
                <th><Lbl>Given to</Lbl></th>
                <th><Lbl>Due</Lbl></th>
                <th><Lbl>Status</Lbl></th>
                <th className="tr"><Lbl>Time</Lbl></th>
              </tr>
            </thead>
            <tbody>
              {rows.length ? (
                rows.map((r) => {
                  const [cls, word] = status(r);
                  const d = r.due ? dueText(r.due, today) : null;
                  const open = r.kind === 'task' ? () => setTask(r.id) : r.kind === 'proposed' ? () => nav('/team/review') : undefined;
                  return (
                    <tr key={`${r.kind}:${r.id}`} className={open ? 'row' : undefined} onClick={open}>
                      <td>
                        <b>{r.title}</b>
                        <div className="sub">{r.client ? r.client.name : 'Internal'}</div>
                      </td>
                      <td>
                        <span className="chip info">{srcLabel(r.source)}</span>
                      </td>
                      <td>
                        {r.assignee ? (
                          <span className="split" style={{ gap: 6 }}>
                            <Avatar user={r.assignee} /> {firstName(r.assignee.name)}
                          </span>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td className="num">{d ? d.label : '-'}</td>
                      <td>
                        <span className={`chip ${cls}`}>{word}</span>
                      </td>
                      <td className="tr sub" style={{ color: 'var(--sub)' }}>
                        {time(r.at)}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={6}>
                    <div className="empty">
                      <b>Nothing yet today</b>
                      {!src.length ? null : `Nothing from ${src.length > 1 ? 'these sources' : 'this source'} today.`}
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      {task ? <TaskPanel id={task} onClose={() => setTask(null)} /> : null}
    </>
  );
}
